<template>
	<v-row class="full-height inventory-items-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="inventory-items-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search inventory items..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="inventory-items-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Show Inactive Filter -->
						<v-col cols="6" sm="3" md="2">
							<v-checkbox
								v-model="searchForm.allowInactive"
								label="Show Inactive"
								hide-details
								class="mt-0 pt-0"
								@change="onFilterChange"
							></v-checkbox>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Actions -->
						<v-col cols="12" sm="auto" class="d-flex justify-end">
							<v-btn
								v-if="canManageInventory"
								color="success"
								depressed
								@click="$router.push('/inventory/items/new')"
								:block="$vuetify.breakpoint.xsOnly"
							>
								<v-icon left small>mdi-plus</v-icon>
								New Inventory Item
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 inventory-items-table-container">
			<v-data-table
				:headers="headers"
				:items="inventoryItems"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="inventory-items-data-table"
				@click:row="navigateToItem"
				must-sort
				fixed-header
			>
				<!-- Category Column -->
				<template v-slot:item.systemCategoryId="{ item }">
					{{ $systemCategories.getName(item.systemCategoryId) }}
				</template>

				<!-- Style Code Column -->
				<template v-slot:item.styleCode="{ item }">
					{{ item.styleCode }}
				</template>

				<!-- Description Column -->
				<template v-slot:item.description="{ item }">
					{{ item.description }}
				</template>

				<!-- Inactive Date Column -->
				<template v-slot:item.inactiveDate="{ item }">
					{{ formatDate(item.inactiveDate) }}
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
						<p class="mt-2 grey--text">No inventory items found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "InventoryItemsList",
	data() {
		return {
			canManageInventory: false,
			loading: true,
			searchForm: {
				keywords: "",
				allowInactive: false,
			},
			inventoryItems: [],
			totalCount: 0,
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["systemCategoryId"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Category",
					value: "systemCategoryId",
					sortable: true,
					width: "18%",
				},
				{
					text: "TL Style Code",
					value: "styleCode",
					sortable: true,
					width: "20%",
				},
				{
					text: "Description",
					value: "description",
					sortable: true,
					width: "44%",
				},
				{
					text: "Inactive Date",
					value: "inactiveDate",
					sortable: true,
					width: "18%",
				},
			],
		};
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/inventoryItemsListState"];
					if (savedState) {
						this.tableOptions.page = savedState.offset / 15 + 1; // Convert offset to 1-indexed page
						this.searchForm.keywords = savedState.keywords || "";
						this.searchForm.allowInactive = savedState.allowInactive || false;
						if (savedState.sortBy) {
							this.tableOptions.sortBy = [savedState.sortBy];
						}
						if (savedState.sortDesc !== undefined) {
							this.tableOptions.sortDesc = [savedState.sortDesc];
						}
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		formatDate(date) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				systemCategoryId: "systemCategoryId",
				styleCode: "styleCode",
				description: "description",
				inactiveDate: "inactiveDate",
			};
			return sortFieldMap[columnValue] || "systemCategoryId";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build where clause for inactive filter
			const where = {
				and: [],
			};

			// Add inactive filter
			if (!this.searchForm.allowInactive) {
				where.and.push({
					inactiveDate: { eq: null },
				});
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "systemCategoryId";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm: keyword || null,
				where: where.and.length > 0 ? where : null,
			};
		},

		async fetchData() {
			this.loading = true;
			try {
				const variables = this.buildQueryVariables();
				const res = await this.$apollo.query({
					query: gql`
						query GetInventoryListings(
							$offset: Int!
							$take: Int!
							$searchTerm: String
							$order: [InventoryItemListingSortInput!] = [{ systemCategoryId: ASC }]
							$where: InventoryItemListingFilterInput = null
						) {
							inventoryListings(
								skip: $offset
								take: $take
								order: $order
								searchTerm: $searchTerm
								where: $where
							) {
								totalCount
								items {
									inventoryItemId
									systemCategoryId
									styleCode
									description
									inactiveDate
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables,
					fetchPolicy: "no-cache",
				});

				this.totalCount = res.data.inventoryListings.totalCount;
				this.inventoryItems = res.data.inventoryListings.items;
				this.loading = false;

				// Auto-navigate if exactly one result is found from a search with exact match
				if (
					this.searchForm.keywords &&
					this.searchForm.keywords.trim() !== "" &&
					this.inventoryItems.length === 1 &&
					(this.searchForm.keywords === this.inventoryItems[0]?.styleCode ||
						this.searchForm.keywords === this.inventoryItems[0]?.description)
				) {
					this.navigateToItem(this.inventoryItems[0]);
				}
			} catch (error) {
				console.error("Error fetching inventory items:", error);
				this.loading = false;
			}
		},

		navigateToItem(item) {
			this.$router.push(`/inventory/items/${item.inventoryItemId}`);
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},
	},
	mounted() {
		const user = this.$store.getters.userInfo;
		this.canManageInventory =
			hasPermission(user, SystemPermissions.Inventory_InventoryItems_Create_Update_Delete) ||
			user.isAdmin;
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveInventoryItemsListState", {
			offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
			keywords: this.searchForm.keywords,
			allowInactive: this.searchForm.allowInactive,
			sortBy: this.tableOptions.sortBy[0],
			sortDesc: this.tableOptions.sortDesc[0],
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.inventory-items-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.inventory-items-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.inventory-items-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.inventory-items-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.inventory-items-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 800px;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Make rows clickable
	::v-deep tbody tr {
		cursor: pointer;
		transition: background-color 0.2s;

		&:hover {
			background-color: $color-gray-light !important;
		}
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
